import { NavigationCard, NavigationCardTypes, Stack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { IconographyTypes } from '@genial/icons';
import { useNavigation } from '@react-navigation/native';
import React from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';

import { SignedInNavigationScreenNavigationProp } from '../../../../routes/types';
import {
  customerConsortiumItemSelectedState,
  customerConsortiumSectionStatusState,
} from '../../../../store/consortium/atom';
import { sessionState } from '../../../../store/session/atom';
import { FeatureToggle, useUnleashFlags } from '../../../../utils/useUnleashFlags';
import { Divider } from '../Divider';
import { SectionInnerCard } from '../SectionInnerCard';
import { SectionOuterCard } from '../SectionOuterCard';
import { SectionTitle } from '../SectionTitle';

interface OperationsAndMovementProps {
  isMobile?: boolean;
  isTablet?: boolean;
}

export const OperationsAndMovement: React.FC<OperationsAndMovementProps> = ({
  isMobile,
  isTablet,
}) => {
  const {
    b2bShowModuleTrading,
    b2bShowModuleConsortium,
    b2bShowNewSubscriptionPage,
    b2bShowNewAvailableProductsPage,
    b2bShowModuleProductManagement,
  } = useUnleashFlags(
    FeatureToggle.Trading,
    FeatureToggle.Consortium,
    FeatureToggle.NewSubscriptionPage,
    FeatureToggle.NewAvailableProductsPage,
    FeatureToggle.ProductControl
  );
  const { userProfile } = useRecoilValue(sessionState);
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const resetConsortiumSectionStatus = useResetRecoilState(customerConsortiumSectionStatusState);
  const resetConsortiumItemSelected = useResetRecoilState(customerConsortiumItemSelectedState);

  const onDrayTradePress = () => {
    navigate('Trading');
  };
  const onConsortiumPress = () => {
    resetConsortiumSectionStatus();
    resetConsortiumItemSelected();
    navigate('Consortium');
  };
  const onSubscriptionsPress = () => {
    if (!b2bShowNewSubscriptionPage) {
      return navigate({
        name: 'AngularFrame_VoluntaryEvents',
        params: { state: 'app.eventosVoluntarios' },
      });
    }
    navigate('Subscription');
  };

  const onProductsManagementPress = () => {
    if (b2bShowModuleProductManagement) {
      navigate('ProductsManagement');
    } else {
      navigate({
        name: 'AngularFrame_ProductControl',
        params: { state: 'app.controleProduto.busca' },
      });
    }
  };

  const onTransactionFlowPress = () => navigate('TransactionFlow');
  const onDividendHistoryPress = () => navigate('DividendHistory');

  const onAvailableProductsPress = () => {
    if (!b2bShowNewAvailableProductsPage) {
      return navigate({
        name: 'AngularFrame_AvailableProducts',
        params: { state: 'app.produtosDisponiveis.fundosInvestimentos' },
      });
    }
    navigate('AvailableProducts');
  };

  const onLifeInsurancePress = () =>
    navigate({
      name: 'AngularFrame_LifeInsurance',
      params: { state: 'app.seguros.inicio' },
    });

  const rentabilityOptions = () => {
    const options: NavigationCardTypes.Props[] = [
      {
        title: 'Subscrição',
        description:
          'Amplie os investimentos dos seus clientes adquirindo novas ações e garanta a participação deles em empresas em crescimento.',
        buttonText: 'Acessar',
        icon: 'Subscriptions' as IconographyTypes.Option,
        testId: 'subscriptions',
        buttonOnPress: onSubscriptionsPress,
      },
      {
        title: 'Proventos',
        description:
          'Garanta que seus clientes recebam parte dos lucros das empresas nas quais investem através de dividendos e juros sobre capital próprio.',
        buttonText: 'Acessar',
        icon: 'BusinessAndFinance' as IconographyTypes.Option,
        testId: 'earnings',
        buttonOnPress: onDividendHistoryPress,
      },
    ];

    if (b2bShowModuleConsortium) {
      options.unshift({
        title: 'Consórcio',
        description:
          'Envie, acompanhe e gerencie facilmente recomendações de consórcio para seus clientes, garantindo um processo ágil e transparente.',
        buttonText: 'Acessar',
        icon: 'Consortium' as IconographyTypes.Option,
        testId: 'Consortium',
        buttonOnPress: onConsortiumPress,
      });
    }

    if (b2bShowModuleTrading) {
      options.unshift({
        title: 'Daytrade',
        description:
          'Acompanhe com agilidade as aplicações de seus clientes e as variações diárias do mercado.',
        buttonText: 'Acessar',
        icon: 'DayTrade' as IconographyTypes.Option,
        testId: 'DayTrade',
        buttonOnPress: onDrayTradePress,
      });
    }

    return (
      <Stack
        direction={isMobile ? 'column' : 'row'}
        space={isMobile ? undefined : Spacing['stack.nano']}>
        {options.map((navigationOption, index) => (
          <React.Fragment key={navigationOption.title}>
            <NavigationCard
              {...navigationOption}
              isCompact={isTablet && !isMobile}
              showAsList={isMobile}
            />
            {isMobile && index !== options.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Stack>
    );
  };

  const hasProductControl = userProfile?.masterParceiro || userProfile?.masterGrupo;
  const productControlOption = [
    {
      title: 'Controle de produtos',
      description:
        'Monitore e ajuste seus investimentos em diversos produtos com facilidade e agilidade.',
      buttonText: 'Acessar',
      icon: 'BusinessAndFinance' as IconographyTypes.Option,
      testId: 'earnings',
      buttonOnPress: onProductsManagementPress,
    },
  ];

  const hasLifeInsurance = userProfile?.ExibirSeguros;
  const lifeInsuranceOption = [
    {
      title: 'Seguro de vida',
      description:
        'Amplie os investimentos dos seus clientes adquirindo novas ações e garanta a participação deles em empresas em crescimento.',
      buttonText: 'Acessar',
      icon: 'BusinessAndFinance' as IconographyTypes.Option,
      testId: 'earnings',
      buttonOnPress: onLifeInsurancePress,
    },
  ];

  const portfolioOptions = () => {
    const options: NavigationCardTypes.Props[] = [
      {
        title: 'Fluxo de movimentação',
        description:
          'Acompanhe movimentações em tempo real ou consulte o histórico para visualizar todos os detalhes das transações realizadas por seus clientes.',
        buttonText: 'Acessar',
        icon: 'DayTrade' as IconographyTypes.Option,
        testId: 'DayTrade',
        buttonOnPress: onTransactionFlowPress,
      },
      {
        title: 'Produtos disponíveis',
        description:
          'Verifique os produtos de investimentos disponíveis e obtenha informações detalhadas sobre cada tipo de produto, incluindo características, rentabilidade e prazos.',
        buttonText: 'Acessar',
        icon: 'Subscriptions' as IconographyTypes.Option,
        testId: 'subscriptions',
        buttonOnPress: onAvailableProductsPress,
      },
      ...(hasProductControl ? productControlOption : []),
      ...(hasLifeInsurance ? lifeInsuranceOption : []),
    ];

    return (
      <Stack
        direction={isMobile ? 'column' : 'row'}
        space={isMobile ? undefined : Spacing['stack.nano']}>
        {options.map((navigationOption, index) => (
          <React.Fragment key={navigationOption.title}>
            <NavigationCard
              {...navigationOption}
              isCompact={isTablet && !isMobile}
              showAsList={isMobile}
            />
            {isMobile && index !== options.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Stack>
    );
  };

  return (
    <SectionOuterCard>
      <SectionTitle
        title="Operações e Rendimentos"
        description="Escolha a opção que deseja usar:"
      />
      <SectionInnerCard>{rentabilityOptions()}</SectionInnerCard>
      <SectionTitle
        title="Movimentações e Portfólio"
        description="Escolha a opção que deseja usar:"
      />
      <SectionInnerCard>{portfolioOptions()}</SectionInnerCard>
    </SectionOuterCard>
  );
};
